import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Center } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { useCreateCalendarMutation } from 'src/meetings/hooks/useCreateCalendarMutation';
import { useNavigation } from '@refinedev/core';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useCalendars } from 'src/meetings/hooks/useCalendars';
import { CalendarPlatform } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';

type Props = {
  platform: CalendarPlatform;
};

export const OAuthRedirectPage = ({ platform }: Props) => {
  const [searchParams] = useSearchParams();
  const { createCalendar } = useCreateCalendarMutation();
  const { calendars } = useCalendars();
  const { push } = useNavigation();

  const [createdCalendarId, setCreatedCalendarId] = useState<string | null>(null);

  const code = useMemo(() => searchParams.get('code') ?? '', [searchParams]);
  const state = useMemo(() => searchParams.get('state') ?? '', [searchParams]);
  const error = useMemo(() => searchParams.get('error') ?? '', [searchParams]);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (error && !hasRunRef.current) {
      hasRunRef.current = true;
      notifyAndLogError(
        'The calendar connection was not successful',
        `There was an error connecting your calendar. The provider returned the error: ${error}. Please try again or contact support if the issue persists.`,
        new Error(`OAuth error: ${error}`),
      );
      push('/meetings');
      return;
    }
  }, [error, push]);

  useEffect(() => {
    if (!code || hasRunRef.current) return;
    hasRunRef.current = true;
    (async () => {
      try {
        const calendar = await createCalendar({ authCode: code, platform, state });
        setCreatedCalendarId(calendar.id);
      } catch (error) {
        notifyAndLogError(
          'Failed to sync calendar',
          'Something went wrong. Please try again.',
          error,
        );
        push('/meetings');
      }
    })();
  }, [code, createCalendar, push]);

  const createdCalendar = calendars.find(c => c.id === createdCalendarId);
  useEffect(() => {
    if (!createdCalendarId || !createdCalendar) {
      return;
    }

    if (createdCalendar.status === 'DISCONNECTED') {
      notifyAndLogError(
        'Failed to connect calendar',
        'Please try again later.',
        new Error('Calendar connection resulted in DISCONNECTED status'),
        { calendarId: createdCalendarId },
      );
    }

    push('/meetings');
  }, [createdCalendarId, createdCalendar, push]);

  return (
    <Center h="calc(100vh - 72px)">
      <PageLoader />
    </Center>
  );
};
